import { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { DataTable } from "~components/DataTable/DataTable.tsx";
import { PermissionNames } from "~enums/permissions.ts";
import { useHandlePagination } from "~hooks/useHandlePagination.ts";
import { useSportConfiguration } from "~hooks/useSportConfiguration.ts";
import { AddSeasonStageModal } from "~pages/SeasonStages/SeasonStageListPage/components/AddSeasonStageModal.tsx";
import {
  defaultSeasonStageExportFieldNames,
  seasonStageExportFields,
} from "~pages/SeasonStages/SeasonStageListPage/constants/exportFields.ts";
import { useDeleteSeasonStage } from "~pages/SeasonStages/SeasonStageListPage/hooks/useDeleteSeasonStage.ts";
import { useExportSeasonStages } from "~pages/SeasonStages/SeasonStageListPage/hooks/useExportSeasonStages.ts";
import { useFetchSeasonStages } from "~pages/SeasonStages/SeasonStageListPage/hooks/useFetchSeasonStages.ts";
import { useGetSeasonStageTableColumns } from "~pages/SeasonStages/SeasonStageListPage/hooks/useGetSeasonStageTableColumns.tsx";
import { useImportSeasonStages } from "~pages/SeasonStages/SeasonStageListPage/hooks/useImportSeasonStages.ts";

const DEFAULT_SORTER = { columnKey: "stageOrder", order: "ascend" as const };

const SeasonStageListPage = () => {
  const { sport, customerId, organizationId, seasonId } = useParams();
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const paginationConfig = useHandlePagination(20);
  const [searchText, setSearchText] = useState("");
  const [massEditMode, setMassEditMode] = useState(false);
  const [isAddSeasonStageModalOpen, setIsAddSeasonStageModalOpen] = useState(false);
  const [sortBy, setSortBy] = useState<string | null>(null);
  const { uiConfiguration } = useSportConfiguration();
  const dataTableUiConfiguration = uiConfiguration.pages.seasonStages.listPage;
  const dataTableImportConfiguration = uiConfiguration.pages.seasonStages.listPage.importConfig;
  const columns = useGetSeasonStageTableColumns(massEditMode);
  const { mutateAsync: startImporting } = useImportSeasonStages();
  const { mutateAsync: startExport, isPending: isExportPending } = useExportSeasonStages();
  const { mutateAsync: deleteSeasonStage, isPending: isDeletePending } = useDeleteSeasonStage();

  const handleSearch = useCallback(
    (value: string) => {
      paginationConfig.resetPagination();
      setSearchText(value);
    },
    [paginationConfig],
  );

  const listParams = useMemo(
    () => ({
      limit: paginationConfig.pageSize.toString(),
      offset: paginationConfig.offset.toString(),
      sortBy: sortBy || "",
      nameLocalContains: searchText,
    }),
    [paginationConfig.offset, paginationConfig.pageSize, searchText, sortBy],
  );

  const { data, refetch, isPending } = useFetchSeasonStages({
    customParams: listParams,
    enabled: sortBy !== null,
  });

  const getNavigateUrl = (stageCode: string) => `/${customerId}/admin/${sport}/${organizationId}/seasons/${seasonId}/stages/${stageCode}`;

  const onMassEditModeChangedHandler = useCallback((checked: boolean) => {
    setMassEditMode(checked);
  }, []);

  return (
    <>
      <AddSeasonStageModal isOpen={isAddSeasonStageModalOpen} setIsOpen={setIsAddSeasonStageModalOpen} />
      <DataTable
        handleRefresh={() => {
          void refetch();
        }}
        title={formatMessage({ id: "stages" })}
        columns={columns}
        rowActions={{
          base: {
            manage: {
              onClick: (row) => navigate(getNavigateUrl(row.stageCode)),
            },
            delete: {
              onClick: (row) => {
                void deleteSeasonStage(row.stageCode);
              },
              itemName: (row) => row.nameLocal || row.stageCode,
              loading: isDeletePending,
            },
          },
        }}
        rowKey="stageCode"
        rowNameKey="nameLocal"
        dataSource={data?.data || []}
        columnChooserLocalStorageKey={`${sport}.tables.settings.columns.seasonStages`}
        isDataLoading={isPending}
        handleAdd={() => setIsAddSeasonStageModalOpen(true)}
        addButtonIntlKey="stages.add"
        searchConfig={{
          handleSearch,
          searchText,
        }}
        onMassEditModeChangedHandler={onMassEditModeChangedHandler}
        massEditMode={massEditMode}
        columnSortConfig={{
          sorterLocalStorageKey: `${sport}.tables.settings.sort.seasonStages`,
          defaultSorter: DEFAULT_SORTER,
          onSortChange: setSortBy,
        }}
        exportConfig={{
          handleExport: startExport,
          loading: isExportPending,
          defaultFields: defaultSeasonStageExportFieldNames,
          groupedFields: seasonStageExportFields,
          localStorageKey: "export.seasonStages",
        }}
        importConfig={{
          ...dataTableImportConfiguration,
          handleImport: startImporting,
        }}
        permissionNames={[PermissionNames.STAGE]}
        dataTableUiConfiguration={dataTableUiConfiguration}
        paginationConfig={{ ...paginationConfig, hasNext: data?.hasNextLink }}
      />
    </>
  );
};

export default SeasonStageListPage;
